import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Grid, Box } from '@mui/material';
import routeNames from '../../../../routes/routeNames';
import Engagement from './engagement';
import {
    TOTAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
    FIELD_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
    DIGITAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
} from './constants';
import EngagementSkeleton from './skeleton';
import { generateChartColorBasisOfChange } from '../../../../utility';

const Engagements = (props) => {
    const navigate = useNavigate();
    const [hovered, setHovered] = useState(-1);
    const [showDaysBarChart, setShowDaysBarChart] = useState(true);
    const [showMonthsBarChart, setShowMonthsBarChart] = useState(false);
    const [showAreaChart, setShowAreaChart] = useState(false);

    const { totalSummary, fieldSummary, digitalSummary, isLoading } =
        useSelector((state) => state.engagement);
    const { digitalBarData } = digitalSummary;
    const { totalBarData } = totalSummary;
    const { fieldBarData } = fieldSummary;
    const { startDateRange, endDateRange } = useSelector(
        (state) => state.filter,
    );

    const {
        company: { id: companyId },
    } = useSelector((state) => state.company);

    const hideTotalEngagement = [97, 103, 109].includes(companyId);
    const hideFieldEngagement = [97, 102, 103, 109, 113].includes(companyId);

    const engagementTitles = {
        default: {
            total: 'Total Physician Engagement',
            field: 'Field Physician Engagement',
            digital: 'Digital Physician Engagement',
        },
        150: { field: 'MSL Engagement' },
        97: { digital: 'Digital Engagement' },
    };

    const getTitle = (engagementType) =>
        (engagementTitles[companyId] &&
            engagementTitles[companyId][engagementType]) ||
        engagementTitles.default[engagementType];

    const MouseEnter = (index) => {
        setHovered(index);
    };

    const MouseLeave = () => {
        setHovered(-1);
    };

    const handleEngagement = (engagementType) => {
        navigate(engagementType);
    };

    const handleCharts = () => {
        const startDate = new Date(startDateRange);
        const endDate = new Date(endDateRange);
        const differenceInMilliseconds = Math.abs(endDate - startDate);
        const differenceInDays = Math.ceil(
            differenceInMilliseconds / (1000 * 60 * 60 * 24),
        );
        const totalMonths = Math.floor(differenceInDays / 30);

        if (totalMonths > 6) {
            setShowAreaChart(true);
            setShowMonthsBarChart(false);
            setShowDaysBarChart(false);
        } else if (differenceInDays > 31 && totalMonths <= 6) {
            setShowAreaChart(false);
            setShowMonthsBarChart(true);
            setShowDaysBarChart(false);
        } else {
            setShowAreaChart(false);
            setShowMonthsBarChart(false);
            setShowDaysBarChart(true);
        }
    };

    const visibleCount =
        (hideTotalEngagement ? 0 : 1) + (hideFieldEngagement ? 0 : 1) + 1; // digital always visible

    const gridSize = 12 / visibleCount;

    useEffect(() => {
        if (startDateRange && endDateRange) {
            handleCharts();
        }
    }, [startDateRange, endDateRange]);
    return (
        <>
            {/* Total Physician Engagement */}
            {!hideTotalEngagement && (
                <Grid
                    item
                    className="min-height-200px"
                    data-testid="dashboard_engagement_graphs"
                    lg={gridSize}
                    sx={{ zoom: { xs: '50%', lg: '100%' } }}
                    xs={12}
                    onMouseEnter={() => MouseEnter(0)}
                    onMouseLeave={MouseLeave}
                >
                    <Box className="total-engagement-summary-container">
                        {isLoading ? (
                            <EngagementSkeleton key="total-engagement-skeleton" />
                        ) : (
                            <Engagement
                                chartOptions={
                                    showDaysBarChart
                                        ? {
                                              ...TOTAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                              xaxis: {
                                                  ...TOTAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                                  categories:
                                                      totalBarData.categories,
                                              },
                                              colors: [
                                                  hovered === 0
                                                      ? generateChartColorBasisOfChange(
                                                            totalSummary.currentEngagement,
                                                            totalSummary.previousEngagement,
                                                        )
                                                      : '#4EAAF8',
                                              ],
                                          }
                                        : showMonthsBarChart
                                          ? {
                                                ...FIELD_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                                xaxis: {
                                                    ...FIELD_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                                    categories:
                                                        totalBarData.categories,
                                                },
                                                colors: [
                                                    hovered === 0
                                                        ? generateChartColorBasisOfChange(
                                                              totalSummary.currentEngagement,
                                                              totalSummary.previousEngagement,
                                                          )
                                                        : '#4EAAF8',
                                                ],
                                            }
                                          : showAreaChart && {
                                                ...DIGITAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                                xaxis: {
                                                    ...DIGITAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                                    categories:
                                                        totalBarData.categories,
                                                },
                                                colors: [
                                                    hovered === 0
                                                        ? generateChartColorBasisOfChange(
                                                              totalSummary.currentEngagement,
                                                              totalSummary.previousEngagement,
                                                          )
                                                        : '#4EAAF8',
                                                ],
                                            }
                                }
                                chartSeries={totalBarData.barData}
                                chartType={showAreaChart ? 'area' : 'bar'}
                                engagementId={0}
                                engagementSummary={totalSummary}
                                handleClick={() =>
                                    handleEngagement(
                                        routeNames.TOTAL_PHYSICIAN_ENGAGEMENT,
                                    )
                                }
                                selectedId={hovered}
                                title={getTitle('total')}
                            />
                        )}
                    </Box>
                </Grid>
            )}

            {/* Field Physician Engagement */}
            {!hideFieldEngagement && (
                <Grid
                    item
                    className="min-height-200px"
                    lg={gridSize}
                    sx={{ zoom: { xs: '50%', lg: '100%' } }}
                    xs={12}
                    onMouseEnter={() => MouseEnter(1)}
                    onMouseLeave={MouseLeave}
                >
                    <Box className="field-engagement-summary-container">
                        {isLoading ? (
                            <EngagementSkeleton key="field-engagement-skeleton" />
                        ) : (
                            <Engagement
                                chartOptions={
                                    showDaysBarChart
                                        ? {
                                              ...TOTAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                              xaxis: {
                                                  ...TOTAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                                  categories:
                                                      fieldBarData.categories,
                                              },
                                              colors: [
                                                  hovered === 1
                                                      ? generateChartColorBasisOfChange(
                                                            fieldSummary.currentEngagement,
                                                            fieldSummary.previousEngagement,
                                                        )
                                                      : '#4EAAF8',
                                              ],
                                          }
                                        : showMonthsBarChart
                                          ? {
                                                ...FIELD_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                                xaxis: {
                                                    ...FIELD_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                                    categories:
                                                        fieldBarData.categories,
                                                },
                                                colors: [
                                                    hovered === 1
                                                        ? generateChartColorBasisOfChange(
                                                              fieldSummary.currentEngagement,
                                                              fieldSummary.previousEngagement,
                                                          )
                                                        : '#4EAAF8',
                                                ],
                                            }
                                          : showAreaChart && {
                                                ...DIGITAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                                xaxis: {
                                                    ...DIGITAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                                    categories:
                                                        fieldBarData.categories,
                                                },
                                                colors: [
                                                    hovered === 1
                                                        ? generateChartColorBasisOfChange(
                                                              fieldSummary.currentEngagement,
                                                              fieldSummary.previousEngagement,
                                                          )
                                                        : '#4EAAF8',
                                                ],
                                            }
                                }
                                chartSeries={fieldBarData.barData}
                                chartType={
                                    showAreaChart &&
                                    fieldBarData?.barData[0]?.data?.length > 1
                                        ? 'area'
                                        : 'bar'
                                }
                                engagementId={1}
                                engagementSummary={fieldSummary}
                                handleClick={() =>
                                    handleEngagement(
                                        routeNames.FIELD_PHYSICIAN_ENGAGEMENT,
                                    )
                                }
                                selectedId={hovered}
                                title={getTitle('field')}
                            />
                        )}
                    </Box>
                </Grid>
            )}

            {/* Digital Physician Engagement */}
            <Grid
                item
                className="min-height-200px"
                lg={gridSize}
                sx={{ zoom: { xs: '50%', lg: '100%' } }}
                xs={12}
                onMouseEnter={() => MouseEnter(2)}
                onMouseLeave={MouseLeave}
            >
                <Box className="digital-engagement-summary-container">
                    {isLoading ? (
                        <EngagementSkeleton key="digital-engagement-skeleton" />
                    ) : (
                        <Engagement
                            chartOptions={
                                showDaysBarChart
                                    ? {
                                          ...TOTAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                          xaxis: {
                                              ...TOTAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                              categories:
                                                  digitalBarData.categories,
                                          },
                                          colors: [
                                              hovered === 2
                                                  ? generateChartColorBasisOfChange(
                                                        digitalSummary.currentEngagement,
                                                        digitalSummary.previousEngagement,
                                                    )
                                                  : '#4EAAF8',
                                          ],
                                      }
                                    : showMonthsBarChart
                                      ? {
                                            ...FIELD_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                            xaxis: {
                                                ...FIELD_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                                categories:
                                                    digitalBarData.categories,
                                            },
                                            colors: [
                                                hovered === 2
                                                    ? generateChartColorBasisOfChange(
                                                          digitalSummary.currentEngagement,
                                                          digitalSummary.previousEngagement,
                                                      )
                                                    : '#4EAAF8',
                                            ],
                                        }
                                      : showAreaChart && {
                                            ...DIGITAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS,
                                            xaxis: {
                                                ...DIGITAL_PHYSICIAN_ENGAGEMENT_CHART_OPTIONS.xaxis,
                                                categories:
                                                    digitalBarData.categories,
                                            },
                                            colors: [
                                                hovered === 2
                                                    ? generateChartColorBasisOfChange(
                                                          digitalSummary.currentEngagement,
                                                          digitalSummary.previousEngagement,
                                                      )
                                                    : '#4EAAF8',
                                            ],
                                        }
                            }
                            chartSeries={digitalBarData.barData}
                            chartType={showAreaChart ? 'area' : 'bar'}
                            engagementId={2}
                            engagementSummary={digitalSummary}
                            handleClick={() =>
                                handleEngagement(
                                    companyId === 97
                                        ? routeNames.DIGITAL_ENGAGEMENT
                                        : routeNames.DIGITAL_PHYSICIAN_ENGAGEMENT,
                                )
                            }
                            selectedId={hovered}
                            title={getTitle('digital')}
                        />
                    )}
                </Box>
            </Grid>
        </>
    );
};

export default Engagements;
